/**
 * When Vitalik's *day* ends, as opposed to when the calendar day ends.
 *
 * He journals and checks in at the end of an evening, and an evening regularly
 * runs past midnight. At 01:40 he is still finishing Saturday — but the
 * calendar already says Sunday, so every screen that asked for "today" quietly
 * opened the wrong day and he filled it in without noticing.
 *
 * The rule: anything **self-reported** is stamped with the day he is still
 * living, and that day rolls over at 03:00. Money, sales, bank statements and
 * calendar events are *events with their own real timestamps* and keep the
 * calendar day — shifting those would put a 01:00 purchase in the wrong day and
 * disagree with Monobank.
 *
 * Mirror of `day_in_progress()` in `backend/jarvis/api/deps.py`; the hour lives
 * in both files on purpose, because the page has to know which day to open
 * before it has asked the server anything.
 */

/** The hour a personal day rolls over. Keep in step with the backend constant. */
export const DAY_ROLLOVER_HOUR = 3;

/** Local calendar day of a `Date` as ISO `YYYY-MM-DD` (the `sv` locale is that format). */
function isoDay(at: Date): string {
  return at.toLocaleDateString("sv");
}

/**
 * The day the user is still living, as ISO `YYYY-MM-DD`.
 *
 * Equal to the calendar day from 03:00 onwards; before that it is the previous
 * day. Use it anywhere a screen needs "today" for something he types about
 * himself — the journal, the wellbeing check-in.
 */
export function dayInProgress(at: Date = new Date()): string {
  const d = new Date(at);
  if (d.getHours() < DAY_ROLLOVER_HOUR) {
    d.setDate(d.getDate() - 1);
  }
  return isoDay(d);
}

/** The real calendar day, ISO `YYYY-MM-DD` — for anything with its own timestamp. */
export function calendarDay(at: Date = new Date()): string {
  return isoDay(at);
}

/**
 * True in the small hours, while the day in progress is yesterday's date.
 *
 * The UI must say so: a journal that opens on «15 серпня» at 01:40 looks like a
 * bug unless the screen explains that the day has not turned over yet.
 */
export function isPastMidnightCarryOver(at: Date = new Date()): boolean {
  return at.getHours() < DAY_ROLLOVER_HOUR;
}

/** Shift an ISO day by whole days, staying on the local calendar. */
export function shiftIsoDay(day: string, delta: number): string {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + delta);
  return isoDay(d);
}

/**
 * The Monday of the week `day` belongs to, ISO `YYYY-MM-DD`.
 *
 * Monday-first because every calendar he reads is: `weekdayShort` in
 * `lib/format.ts` starts the week at «Пн», and a week opening on Sunday would
 * disagree with the header drawn above it. `getDay()` counts from Sunday, so
 * `(getDay() + 6) % 7` is how many days back Monday is.
 */
export function startOfWeek(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return isoDay(d);
}

/** The seven ISO days of the week that starts at `monday`, in order. */
export function weekDays(monday: string): string[] {
  return Array.from({ length: 7 }, (_, i) => shiftIsoDay(monday, i));
}
